// `pointerdown` covers mouse, pen and touch, so listening for `mousedown` and
// `touchstart` as well would only unlock the same gesture several times over.
const UNLOCK_EVENTS = ['keydown', 'pointerdown']

/**
 * Keeps the notification/ringtone elements playable under autoplay policies.
 *
 * Chromium and Firefox unlock the page on the first user gesture; WebKit unlocks
 * each element only when it is loaded or played during one. So the first gesture
 * `load()`s every registered element, which unlocks it without making a sound.
 * Don't probe by playing at volume 0 instead: WebKit exempts volume 0 from the
 * gesture rule, so the probe plays on page load and the agent hears it.
 */
class AudioPlaybackService {
  constructor () {
    this._elements = new Set()
    this._played = new Set()
    this._pending = new Set()
    this._lastErrorName = null
    this._unlocked = false
    this._listening = false
    this._onInteraction = () => {
      this.unlock()
    }
  }

  // Name of the last playback failure, so callers can log why a sound was not
  // audible instead of assuming an autoplay refusal.
  get lastErrorName () {
    return this._lastErrorName
  }

  get unlocked () {
    // An element that has reached playback proves audio works on this page, and
    // that fact cannot be undone by a later refusal — otherwise one blocked ring
    // would leave the diagnostics reporting `unlocked: false` forever, since
    // nothing recomputes it once the unlock listeners have detached.
    return this._unlocked || this._played.size > 0
  }

  register (element) {
    if (!element) {
      return
    }

    this._elements.add(element)
  }

  /**
   * Unlock the registered elements on the first user gesture.
   */
  start () {
    if (this._listening) {
      return
    }

    this._listening = true

    UNLOCK_EVENTS.forEach(event => {
      window.addEventListener(event, this._onInteraction, { passive: true })
    })
  }

  stop () {
    this._listening = false

    UNLOCK_EVENTS.forEach(event => {
      window.removeEventListener(event, this._onInteraction)
    })
  }

  /**
   * Drop tracked elements. The store rebuilds the audio elements on every layout
   * mount (login, account switch), so the previous ones must not be kept alive
   * here.
   */
  reset () {
    this.stop()
    this._elements.clear()
    this._played.clear()
    this._pending.clear()
    this._unlocked = false
  }

  /**
   * Must run synchronously inside the gesture handler: WebKit only unlocks an
   * element loaded while the gesture is being processed.
   */
  unlock () {
    let skipped = false

    this._elements.forEach(element => {
      // `load()` aborts playback, so a ring that is playing or about to play is
      // left for the next gesture.
      if (!element.paused || this._pending.has(element)) {
        skipped = true
        return
      }

      element.load()
    })

    this._unlocked = true

    if (!skipped) {
      this.stop()
    }
  }

  /**
   * Play an element. Never throws: resolves to false when the browser refused,
   * so the caller can fall back to a notification the agent can actually
   * perceive.
   */
  play (element) {
    if (!element) {
      return Promise.resolve(false)
    }

    // Added synchronously, before playback starts, so a gesture landing in the
    // meantime does not `load()` (and so abort) the ring we are starting.
    this._pending.add(element)

    return Promise.resolve()
      .then(() => {
        // Every call rings from the top, so a ring cut short does not leave the
        // next one starting part-way into the tone. Kept inside the chain so a
        // throwing assignment surfaces as a resolved `false` rather than escaping
        // to callers that have no catch.
        if (element.paused) {
          element.currentTime = 0
        }

        return element.play()
      })
      .then(() => {
        this._pending.delete(element)
        this._played.add(element)
        this._lastErrorName = null
        this._unlocked = true
        return true
      })
      .catch(err => {
        this._pending.delete(element)
        this._lastErrorName = err?.name || 'Error'

        // An AbortError means something paused the element while this play() was
        // still pending — the sound was cancelled on purpose, not refused by the
        // browser, so it says nothing about whether audio is playable. Anything
        // else may be an autoplay refusal (NotAllowedError) or an unplayable
        // source (NotSupportedError — a 404 or unreachable API), so name it rather
        // than asserting a cause.
        if (this._lastErrorName !== 'AbortError') {
          this._unlocked = false
          console.warn(`AudioPlaybackService: playback failed (${this._lastErrorName})`, err)
        }

        return false
      })
  }
}

export default new AudioPlaybackService()
